"use client";

import {
  Children,
  cloneElement,
  type FC,
  type ReactElement,
  type ReactNode,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

import { Popup } from "../Popup";
import {
  SelectList,
  type SelectListOption,
  type SelectListSelectHandler,
} from "../SelectList";
import { IconButton } from "../IconButton";
import type { ContextAction } from "../../types";
import type { ChildrenProp } from "../../props";
import { useClickOutside } from "../../hooks/useClickOutside";
import { Icon } from "../Icon";
import { Modal } from "../Modal";
import { Body } from "../Body";
import { Flex } from "../Flex";
import { Button } from "../Button";
import { useContextActions } from "../../hooks/useContextActions";

export type ContextActionsPopupProps = ChildrenProp & {
  actions: ContextAction[];
  title?: ReactNode;
  applyActive?: boolean;
  disabled?: boolean;
  onAction?: (action: ContextAction) => void;
};

/**
 * ContextActionsPopup component is a popup menu that displays a list of actions
 * that can be performed on a selected item.
 */
export const ContextActionsPopup: FC<ContextActionsPopupProps> = ({
  actions,
  applyActive = true,
  title,
  children = (
    <IconButton
      icon="threeDots"
      variant="opaque"
      border="default"
      size="xs"
      rounded
    />
  ),
  disabled = false,
  onAction,
}) => {
  const { t } = useTranslation();
  const {
    handleActionClick,
    isConfirmationActive,
    setIsConfirmationActive,
    handleDeleteConfirm,
    confirmationTitle,
  } = useContextActions({ actions, title, onAction });

  const [isActive, setIsActive] = useState(false);
  const popupRef = useRef<HTMLDivElement>(null);
  const ref = useRef<HTMLButtonElement>(null);

  const childrenArray = Children.toArray(children);
  const firstChild = childrenArray?.[0] as ReactElement<any>;

  useClickOutside<HTMLDivElement | HTMLButtonElement>([popupRef, ref], () => {
    handleClose?.();
  });

  const handleSelect: SelectListSelectHandler = (listOption) => {
    const action = actions.find((action) => action.label === listOption.label);

    if (!action) {
      return;
    }

    handleActionClick(action);
    setIsActive(false);
  };

  const handleClose = () => {
    setIsActive(false);
  };

  const options: SelectListOption[] = (() => {
    const options: SelectListOption[] = [];

    actions.forEach((action) => {
      let leading: ReactNode | undefined;
      let variant: SelectListOption["variant"] = "default";

      if (action.type === "delete") {
        leading = <Icon icon="delete" size="s" />;
        variant = "error";
      }

      options.push({
        leading,
        label: action.label,
        value: action.label,
        variant,
      });
    });

    return options;
  })();

  return (
    <>
      <Popup
        ref={popupRef}
        active={isActive}
        onClose={handleClose}
        content={<SelectList options={options} onSelect={handleSelect} />}
        applyActive
        escape
        size="minimal"
        placement="bottomLeft"
      >
        {firstChild &&
          cloneElement(firstChild, {
            ref,
            ...(applyActive ? { active: isActive } : {}),
            ...(disabled ? { disabled } : {}),
            onClick: () => setIsActive(true),
          })}
        {childrenArray.slice(1)}
      </Popup>

      {isConfirmationActive && (
        <Modal placement="center">
          <Body>
            {confirmationTitle
              ? t("contextActionsPopup.deleteConfirmation", {
                  title: confirmationTitle,
                })
              : t("contextActionsPopup.deleteConfirmationDefault")}
          </Body>
          <Flex justify="space-between" align="flex-end" gap="s">
            <Button
              rel="noopener noreferrer"
              variant="background"
              border="default"
              onClick={() => setIsConfirmationActive(false)}
            >
              {t("contextActionsPopup.cancel")}
            </Button>
            <Button
              rel="noopener noreferrer"
              variant="error"
              trailing={<Icon icon="delete" size="s2" />}
              onClick={handleDeleteConfirm}
            >
              {t("contextActionsPopup.delete")}
            </Button>
          </Flex>
        </Modal>
      )}
    </>
  );
};
